import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

const MOODS = [
  { emoji: '😊', label: 'happy' },
  { emoji: '😐', label: 'okay' },
  { emoji: '😢', label: 'sad' },
  { emoji: '🤩', label: 'excited' },
  { emoji: '😴', label: 'tired' },
];

interface Summary {
  id: number;
  date: string;
  summaryText: string;
  mood: string;
}

const DailySummary: React.FC<{ kidId?: number | string }> = ({ kidId }) => {
  const { userId } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const [todaySummary, setTodaySummary] = useState<Summary | null>(null);
  const [history, setHistory] = useState<Summary[]>([]);
  const [text, setText] = useState('');
  const [mood, setMood] = useState('happy');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchToday();
    fetchHistory();
  }, []);

  const fetchToday = async () => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/summary/today`);
      if (res.status === 200 && res.data) {
        setTodaySummary(res.data);
        setText(res.data.summaryText);
        setMood(res.data.mood || 'happy');
      }
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/summary`);
      setHistory(res.data);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/api/kids/${effectiveKidId}/summary`, { summaryText: text, mood });
      setSaved(true);
      fetchToday();
      fetchHistory();
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Failed to save summary');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <main className="space-y-6">
        <h1 className="text-xl font-display font-extrabold text-gray-800">📝 Daily Summary</h1>
        {/* Today's entry */}
        <div className="bg-white rounded-2xl shadow p-5">
          <h2 className="font-semibold text-gray-700 mb-1">
            How was your day today?
            <span className="text-xs text-gray-400 ml-2">{new Date().toLocaleDateString()}</span>
          </h2>

          {/* Mood selector */}
          <div className="flex gap-3 my-3">
            {MOODS.map((m) => (
              <button
                key={m.label}
                onClick={() => setMood(m.label)}
                className={`text-3xl p-2 rounded-xl transition ${mood === m.label ? 'bg-orange-100 scale-110' : 'hover:bg-gray-100'}`}
                title={m.label}
              >
                {m.emoji}
              </button>
            ))}
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="Write about your day... What did you learn? What was fun?"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
          />

          <div className="flex justify-between items-center mt-3">
            {saved && <span className="text-green-500 text-sm">✓ Saved!</span>}
            {!saved && <span />}
            <button
              onClick={handleSave}
              disabled={saving || !text.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition"
            >
              {saving ? 'Saving...' : todaySummary ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        {/* History — show all entries that are not today's date */}
        {history.filter(s => s.date !== new Date().toISOString().split('T')[0]).length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-600 mb-3">Previous Days</h3>
            <div className="space-y-3">
              {history.filter(s => s.date !== new Date().toISOString().split('T')[0]).map((s) => (
                <div key={s.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-600">
                      {new Date(s.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-xl">
                      {MOODS.find(m => m.label === s.mood)?.emoji || '😊'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{s.summaryText}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DailySummary;
